'use client';

import React, { useCallback, useId, useRef, useState } from 'react';
import {
  ArrowUpTrayIcon,
  CheckCircleIcon,
  DocumentArrowUpIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useTranslations } from 'use-intl';
import { resolveUploadLineAvailability } from '@/utils/product/upload-line-availability';
import { isAcceptedUploadFile, isCsvUploadFile } from '@/utils/file/accepted-upload-file';
import Drawer from '@/components/organisms/drawer';
import Button from '@/components/atoms/button';
import Link from '@/components/atoms/link';
import toast from '@/components/atoms/toaster/helpers/toast';
import { StockStatus } from '@/types/entity/product';
import { pushUploadRejected } from '@/lib/analytics/dataLayer';
import { MAX_UPLOAD_BYTES } from '@shared/types/quick-order/uploadLimits';
import {
  ParsedUploadItem,
  QuickOrderUploadProps,
  UploadFileColumn,
  UploadFileMapping,
  UploadRejectionReason,
} from './types';
import ColumnMapping from './column-mapping';
import UnmatchedRow from './unmatched-row';

const HEADER_HINTS = new Set(['sku', 'code', 'productcode', 'product-code', 'product code']);

// EWM-645 — a compact traffic-light stock badge for each resolved upload row.
// In stock (green) is purchasable; due-soon (amber) and out-of-stock (red) are
// surfaced but excluded from the cart add.
const STOCK_BADGE_PILL: Record<StockStatus, string> = {
  'in-stock': 'bg-green-50 text-green-700',
  'due-soon': 'bg-amber-50 text-amber-700',
  'out-of-stock': 'bg-red-50 text-red-700',
};
const STOCK_BADGE_DOT: Record<StockStatus, string> = {
  'in-stock': 'bg-green-500',
  'due-soon': 'bg-amber-500',
  'out-of-stock': 'bg-red-500',
};
const StockBadge = ({ status }: { status: StockStatus }) => {
  const translate = useTranslations();
  const label =
    status === 'in-stock'
      ? translate('product.in-stock')
      : status === 'due-soon'
        ? translate('product.stock-due-soon')
        : translate('product.out-of-stock');
  return (
    <span
      className={`text-12 inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 font-medium ${STOCK_BADGE_PILL[status]}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${STOCK_BADGE_DOT[status]}`} aria-hidden="true" />
      {label}
    </span>
  );
};

const readAsText = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });

/**
 * EWM-942 — a Frontastic action is a JSON POST, so a workbook has to travel as
 * text. `readAsDataURL` yields `data:<mime>;base64,<payload>`; the payload is
 * what the BFF decodes.
 *
 * It REJECTS rather than returning '' when the reader hands back something that
 * is not a data URL: an empty payload would reach the BFF as "the file is
 * empty", blaming the customer's file for a fault in the browser.
 */
const readAsBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? '');
      const separator = result.indexOf(',');
      if (separator === -1) {
        reject(new Error('quick-order-upload: FileReader did not return a data URL'));
        return;
      }
      resolve(result.slice(separator + 1));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

// EWM-949 — the delimiters a dealer's export actually uses. A semicolon is the
// default CSV separator in every locale where the comma is the decimal mark, a
// tab comes from "copy out of a spreadsheet", a pipe from some ERP exports.
// Assuming a comma put the WHOLE line in the code cell and left the quantity
// undefined — which `parseInt(rawQty ?? '1')` then silently turned into 1.
const DELIMITERS = [',', ';', '\t', '|'] as const;
type Delimiter = (typeof DELIMITERS)[number];
// Reported to analytics (AC-9) — a name, never the raw character.
const DELIMITER_NAMES: Record<Delimiter, string> = { ',': 'comma', ';': 'semicolon', '\t': 'tab', '|': 'pipe' };

/**
 * EWM-949 — split ONE row, honouring quoted cells so a delimiter inside
 * `"TL,4970"` doesn't split it, and `""` inside a quoted cell is a literal
 * quote (RFC 4180). Replaces the old strip-the-outer-quotes approach, which
 * mangled any quoted cell containing the delimiter.
 */
const splitRow = (row: string, delimiter: string): string[] => {
  const cells: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < row.length; i += 1) {
    const char = row[i];
    if (char === '"') {
      if (inQuotes && row[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (char === delimiter && !inQuotes) {
      cells.push(current);
      current = '';
      continue;
    }
    current += char;
  }
  cells.push(current);
  return cells.map((cell) => cell.trim());
};

/**
 * EWM-949 — pick the delimiter from the first non-empty row: whichever
 * candidate yields the most cells. Comma is only beaten by a STRICTLY larger
 * count, so it stays the default on a tie. `null` = no candidate splits the row
 * at all, i.e. the file has no delimiter anywhere; reported as `none` rather
 * than guessed at.
 */
const detectDelimiter = (row: string): Delimiter | null => {
  let best: Delimiter | null = null;
  let bestCount = 1;
  for (const candidate of DELIMITERS) {
    const count = splitRow(row, candidate).length;
    if (count > bestCount) {
      best = candidate;
      bestCount = count;
    }
  }
  return best;
};

/**
 * EWM-949 — "does this single cell read as a product code?" Used ONLY to decide
 * whether a one-column file is a codes-only order list or unrelated text.
 *
 * Narrow by OBSERVATION and deliberately permissive. Every SKU this repo
 * actually carries is an unbroken run of alphanumerics, optionally with a
 * hyphen, dot or underscore: `TL4970`, `QM1537`, `SB2486` (the storefront's own
 * placeholder is "e.g. TL4970"), and the Storybook template's `B01HI7WP0U-1`.
 * There is no SKU-format validator anywhere in this package to borrow a
 * stricter rule from, so this excludes only what is obviously NOT a code — an
 * empty cell, or free text (a space inside the cell: "Order for account
 * E45124", "Please deliver Monday"). Leaning permissive is deliberate: a
 * wrongly ACCEPTED cell costs nothing, because the catalogue lookup that
 * follows rejects it with an accurate "SKU not found", while a wrongly REJECTED
 * one turns a working upload into a reported failure.
 */
const PLAUSIBLE_SKU = /^[A-Za-z0-9][A-Za-z0-9._-]*$/;

/**
 * EWM-949 (PR #1170 review) — is row 1 a header?
 *
 * `HEADER_HINTS` only ever matched the first cell against five literals, so a
 * perfectly ordinary header — `Part Number,Qty`, `Item Code,Qty` — fell through
 * and became a *reported rejected line* on an otherwise-good file. One spurious
 * rejection is cheaper than it was on `dev` (where it was silently accepted and
 * then failed SKU lookup), but it is still a wrong answer.
 *
 * The second test is deliberately narrow, because the opposite error is far
 * worse: a header guess that eats the customer's first real order line loses an
 * order silently. So row 1 counts as a header on the loose test ONLY when both
 * hold:
 *
 *   1. its quantity cell is present and is not a number — no genuine data row
 *      survives with a non-numeric quantity; and
 *   2. its first cell is not a plausible SKU.
 *
 * (2) is what keeps `TL4970,abc` a data row: it stays a
 * `quantity-not-a-number` rejection the customer can see and fix, rather than
 * vanishing as a "header". Only a first cell that could not be a product code
 * in the first place — `Part Number`, `Item Code`, `Description` — is eligible.
 */
const isHeaderRow = (cells: string[]): boolean => {
  const first = cells[0]?.trim() ?? '';
  if (HEADER_HINTS.has(first.toLowerCase())) return true;
  const second = cells[1]?.trim();
  if (second === undefined || second.length === 0) return false;
  return !/^[+-]?\d+$/.test(second) && !PLAUSIBLE_SKU.test(first);
};

// EWM-949 — the quantity a codes-only file implies. Matches the Quick Order
// panel's fallback in useProductHandler, so the two surfaces agree.
const CODES_ONLY_QUANTITY = 1;

interface ParsedCsv {
  items: ParsedUploadItem[];
  /** Delimiter name for the AC-9 signal: comma | semicolon | tab | pipe | none. */
  delimiter: string;
}

// NOTE: EWM-247 shipped quickOrder/addItems (the cart add + failed[] here) and
// EWM-942 shipped quickOrder/uploadFile, which parses .xls/.xlsx server-side and
// returns real { valid, invalid }. This CSV parser stays CLIENT-side on purpose:
// EWM-942 AC-2 requires CSV to behave exactly as it does today, and a browser
// can read a .csv perfectly well without any of that.
//
// EWM-949 — this was a TOTAL function: every row it could shape came back
// `status: 'valid'`, and every row it could not was DELETED by a trailing
// `.filter((item) => item.code.length > 0)`. A malformed file therefore
// reported success with an empty result, and the customer believed an order had
// gone in when nothing had. It now rejects rows — with a reason and the line
// number — and drops nothing, so accepted + rejected always accounts for every
// data row (AC-6 / AC-7). No row is repaired by inventing a value.
const parseCsv = async (file: File): Promise<ParsedCsv> => {
  const text = (await readAsText(file)).replace(/^\uFEFF/, '');
  // Number every line as it appears in the ORIGINAL file — header and blank
  // lines included — so the number reported matches what the customer sees in
  // their spreadsheet (AC-5). Blank lines are not rows, but they still shift
  // the numbering, which is exactly why the index after filtering won't do.
  const rows = text
    .split(/\r?\n/)
    .map((content, index) => ({ line: index + 1, content }))
    .filter((row) => row.content.trim().length > 0);
  if (rows.length === 0) return { items: [], delimiter: 'none' };

  const delimiter = detectDelimiter(rows[0].content);
  // With no delimiter at all every row is a single cell — which lands on the
  // "no quantity in this line" rejection below, rather than a guess.
  const splitChar = delimiter ?? ',';
  const firstCells = splitRow(rows[0].content, splitChar);
  const start = isHeaderRow(firstCells) ? 1 : 0;

  // EWM-949 round 1 (R1-1) — `quantity: 0` is the parser's "the file gave us no
  // usable number here" marker, and the post-add fix flow treats it as one: it
  // asks the customer for the quantity rather than sending a count the BFF will
  // refuse. When the file DID carry a readable quantity — a row whose CODE cell
  // was empty, e.g. `,3` — it is kept, so a corrected row carries the number the
  // customer actually wrote instead of losing it.
  const reject = (line: number, code: string, reasonCode: UploadRejectionReason, quantity = 0): ParsedUploadItem => ({
    code,
    quantity,
    status: 'invalid',
    line,
    reasonCode,
  });

  /** The row's quantity when the file carried a usable one, else 0 ("not read"). */
  const readableQuantity = (raw: string | undefined) =>
    raw !== undefined && /^\d+$/.test(raw) && parseInt(raw, 10) > 0 ? parseInt(raw, 10) : 0;

  // EWM-949 round 3 (R3-3) — a row whose cells are ALL empty (`,`, `;;`) is
  // what Excel writes for a row below the data whose cells were cleared but
  // whose row was never deleted. It is a blank line that happens to contain a
  // delimiter, not something the customer wrote: counting it as a data row
  // rejected it `missing-code`, inflating the rejected total and offering a fix
  // row — with a quantity prompt — for a line that was never theirs.
  //
  // Dropped here rather than in the whitespace filter above because "all cells
  // empty" is only knowable once the delimiter is known, and — exactly like
  // that filter — it runs AFTER the line numbers are fixed, so the numbering a
  // customer reads off their spreadsheet is unaffected (AC-5).
  //
  // Note the deliberate narrowness — the test is on the RAW line, not on the
  // cells `splitRow` produces, and it is not the same test:
  //
  //  * `TL4970,` (a code with an empty quantity cell) carries a code, so it
  //    keeps its round-1 R1-2 behaviour — accepted at quantity 1 with the
  //    default stated.
  //  * `""` — a quoted EMPTY cell — is a row the customer's file actually has
  //    characters on, and `splitRow` unquotes it to `''`. Testing the cells
  //    would swallow it; it stays REPORTED as `missing-code`, which is the
  //    round-0 behaviour the spec "accepts the readable codes and rejects only
  //    the unreadable line" pins and AC-7 relies on.
  //
  // So: a row made of nothing but delimiters and whitespace is blank; a row
  // with any other character on it is a data row.
  const isBlankRow = (content: string) => content.split(splitChar).every((cell) => cell.trim().length === 0);
  const dataRows = rows
    .slice(start)
    .filter(({ content }) => !isBlankRow(content))
    .map(({ line, content }) => ({ line, cells: splitRow(content, splitChar) }));

  // EWM-949 — a file whose EVERY data row is a single cell is a codes-only
  // order list: a different valid shape, not a malformed file. It uploaded at
  // quantity 1 before this ticket and must keep uploading — rejecting it would
  // turn a working upload into a total failure, which is the same harm this
  // ticket exists to remove with the sign flipped. What changes is that the
  // defaulted quantity is STATED on the result (`quantityDefaulted`), never
  // applied silently.
  //
  // The test is FILE-level on purpose. A single-cell row inside a delimited
  // file is a genuinely ragged row — that file HAS a quantity column, so the
  // row is missing one — and still rejects, leaving normal two-column files
  // completely unaffected.
  //
  // Round 1 (R1-2) added a SECOND, row-level path below for the quantity cell
  // that is PRESENT AND EMPTY (`TL4970,`). This file-level test still earns its
  // place: it is the only thing that accepts a file with no delimiter anywhere,
  // and the only place the plausible-SKU rule is applied.
  const codesOnly = dataRows.length > 0 && dataRows.every(({ cells }) => cells.length === 1);

  const items = dataRows.map(({ line, cells }) => {
    const code = cells[0] ?? '';
    const rawQuantity = cells[1];
    if (code.length === 0) return reject(line, code, 'missing-code', readableQuantity(rawQuantity));
    if (codesOnly) {
      // A one-column file that is not a list of codes (free text, an exported
      // note, a title block) still rejects line by line.
      //
      // EWM-949 (PR #1170 review) — this used to reject as 'missing-quantity',
      // which renders "This line has no quantity". For a line like
      // "Order for account E45124" that is simply untrue: the problem is that
      // it is not a product code at all, and a quantity column would not have
      // helped. On a ticket about telling the customer the truth about their
      // file, a wrong reason is the wrong kind of debt.
      if (!PLAUSIBLE_SKU.test(code)) return reject(line, code, 'not-a-product-code');
      return {
        code,
        quantity: CODES_ONLY_QUANTITY,
        status: 'valid' as const,
        line,
        quantityDefaulted: true,
      };
    }
    // A row with FEWER CELLS than the file's delimiter implies is genuinely
    // ragged — the file has a quantity column and this row is missing it — so
    // it still rejects.
    if (rawQuantity === undefined) return reject(line, code, 'missing-quantity');
    // EWM-949 round 1 (R1-2) — but a quantity cell that is PRESENT AND EMPTY is
    // an absence, not a malformation. It is exactly what Excel writes when a
    // customer fills column A of our own downloadable template (header
    // `sku,quantity`) and leaves column B blank: `TL4970,`. Those rows contain a
    // delimiter, so the file-level codes-only test above never matched them and
    // every line was rejected — a file that uploaded fine before this ticket.
    // Same outcome as the codes-only shape: accepted at 1, with the default
    // STATED on the result rather than applied silently.
    if (rawQuantity.length === 0) {
      return { code, quantity: CODES_ONLY_QUANTITY, status: 'valid' as const, line, quantityDefaulted: true };
    }
    // Deliberately strict. `parseInt` read "2 units" as 2 and "abc" as NaN,
    // which `|| 1` then turned into a silent quantity of 1. A quantity we
    // cannot read in full is reported, never coerced. Spreadsheet-shaped
    // numbers ("2.0", "1,000") are EWM-942's parser work, not a guess here.
    if (!/^[+-]?\d+$/.test(rawQuantity)) return reject(line, code, 'quantity-not-a-number');
    const quantity = parseInt(rawQuantity, 10);
    if (quantity <= 0) return reject(line, code, 'quantity-not-positive');
    return { code, quantity, status: 'valid' as const, line };
  });

  return { items, delimiter: delimiter ? DELIMITER_NAMES[delimiter] : 'none' };
};

// EWM-949 — the customer-facing sentence for each PARSE rejection. Distinct on
// purpose: "no quantity", "not a whole number" and "must be at least 1" are
// three different problems for the person fixing the file. The parser stays
// i18n-free so it can be reasoned about (and tested) as a pure function.
const PARSE_REASON_KEYS: Partial<Record<UploadRejectionReason, string>> = {
  'missing-code': 'quick-order.error-row-missing-code',
  'missing-quantity': 'quick-order.error-row-missing-quantity',
  'not-a-product-code': 'quick-order.error-row-not-a-product-code',
  'quantity-not-a-number': 'quick-order.error-row-quantity-not-a-number',
  'quantity-not-positive': 'quick-order.error-row-quantity-not-positive',
};

/**
 * EWM-949 (AC-9) — an upload that accepted NOTHING is the ticket's headline
 * failure, and nothing server-side ever sees it: the parse is entirely
 * client-side, so a dealer whose export is semicolon-delimited shows up only as
 * a support call. Two signals, matching the EWM-714 precedent in
 * `pushCheckoutBlocked`: the dataLayer event aggregates across customers but
 * only those who accepted analytics consent, while the `console.warn` covers
 * everyone yet is collected nowhere (this app has no browser-side error
 * tracking), so it only helps inside a live session.
 *
 * Counts, reason codes and the detected delimiter only — never the file name,
 * the file's contents or a SKU.
 */
const reportZeroAccepted = (items: ParsedUploadItem[], delimiter: string, fileReason = 'no-rows') => {
  // With no rows there is nothing to count, and an empty `reasons` map tells an
  // analyst nothing — so the FILE-level reason takes its place, which also
  // keeps "we don't parse spreadsheets client-side yet" separable from "this
  // CSV was unreadable".
  const reasons = items.length
    ? items.reduce<Record<string, number>>((acc, item) => {
        const reason = item.reasonCode ?? (item.status === 'warning' ? 'unavailable' : 'unknown');
        acc[reason] = (acc[reason] ?? 0) + 1;
        return acc;
      }, {})
    : { [fileReason]: 1 };
  console.warn('[quick-order-upload] EWM-949 — upload accepted 0 lines', {
    rows: items.length,
    delimiter,
    reasons,
  });
  pushUploadRejected({
    total_rows: items.length,
    rejected_rows: items.filter((item) => item.status !== 'valid').length,
    delimiter,
    reasons,
  });
};

// EWM-606 — reject files over 5MB before reading them. readAsText + parse on a
// very large file blocks the main thread and hangs the page.
//
// PR #1189 round-4 review (suggestion 8) — the NUMBER is shared with the BFF
// that enforces it, rather than written out on both sides. This copy is the
// early refusal, not the enforcement: it saves the customer a 5MB upload (and
// its ~33% base64 inflation) only to be told no.
const MAX_FILE_SIZE_BYTES = MAX_UPLOAD_BYTES;

/**
 * EWM-942 round 1 (R1-5) — "the body was too big" can arrive in two shapes, and
 * only one of them is ours.
 *
 * Our own action answers a JSON `{ statusCode: 413, errorName, message }`, which
 * the SDK copies onto the error object — that is the `statusCode` test. But the
 * request has to cross the API hub's front door first, and a proxy that refuses
 * an oversized body answers its own non-JSON page: the SDK falls back to
 * `response.text()`, so the drawer receives a plain string message and NO
 * status code. Without the second test that case fell through to "an error
 * occurred while uploading the file" — a dead end for a customer whose real
 * problem is a file they could split in half.
 *
 * The transport ceiling itself is UNMEASURED (the dev stack was down for this
 * round), which is the reason this reads the body rather than trusting a number
 * we have not proven. The signature is deliberately narrow — the status line or
 * the standard phrases — so an unrelated failure keeps the generic message.
 */
const PAYLOAD_TOO_LARGE_SIGNATURE = /\b413\b|request entity too large|payload too large|body (?:size )?too large/i;

const isPayloadTooLarge = (response: { statusCode?: number; message?: string }): boolean =>
  response.statusCode === 413 || PAYLOAD_TOO_LARGE_SIGNATURE.test(response.message ?? '');

const QuickOrderUpload = ({
  downloadLink,
  isOpen = false,
  onClose,
  addItemDisabled,
  addItem,
  resolveSkus,
  searchProducts,
  backOrdersAllowed = false,
  paymentTerms,
  parseUploadFile,
}: QuickOrderUploadProps) => {
  const translate = useTranslations();
  // PR #1176 review (S3) — `useLocale()` existed only to format the row-count in
  // the "too many rows" copy (`toLocaleString(locale)`). That count is gone (it
  // was off by one against the 0-based row-index cap), so the locale is no
  // longer read here.
  const fileInputRef = useRef<HTMLInputElement>(null);
  const drawerId = useId();

  const [file, setFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [parsedItems, setParsedItems] = useState<ParsedUploadItem[]>([]);
  const [parsing, setParsing] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);
  const [addedToCart, setAddedToCart] = useState(false);
  // EWM-606 — inline validation error (unsupported format / file too large).
  const [uploadError, setUploadError] = useState<string | null>(null);
  // EWM-942 — set when the server could not identify the product-code and/or
  // quantity column of a spreadsheet. While it is set NOTHING has been parsed
  // into order lines: the customer maps their own columns first (AC-6..AC-8).
  const [pendingMapping, setPendingMapping] = useState<{
    file: File;
    columns: UploadFileColumn[];
    headerRowIndex: number;
  } | null>(null);
  // EWM-942 (AC-11) — rows the spreadsheet parser skipped as not-order-lines
  // (notes, totals, title rows above the headings). Reported as a note so
  // nothing is dropped in silence; never a rejection. Round 2 (R2-5) — BLANK
  // rows are deliberately not among them: the parser counts only rows that had
  // something on them, so the copy must not promise a number that includes
  // them.
  const [ignoredRowCount, setIgnoredRowCount] = useState(0);

  const validItems = parsedItems.filter((i) => i.status === 'valid');
  const invalidItems = parsedItems.filter((i) => i.status === 'invalid');
  // EWM-645 — resolved-but-not-purchasable rows (out of stock / due soon).
  const warningItems = parsedItems.filter((i) => i.status === 'warning');
  // Everything the Add-to-Cart step leaves behind: unmatched + unavailable.
  const skippedCount = invalidItems.length + warningItems.length;
  // EWM-949 (PR #1170 review, blocker) — of the invalid rows on the RESULT
  // screen, how many the CART refused (`refused-by-cart`) vs how many the
  // parser/catalogue rejected. The all-refused banner must not tell a customer
  // whose column B was malformed that their account can't buy the product: it
  // counts only the cart's own refusals, and switches to a neutral sentence
  // when the causes are mixed.
  const refusedByCartCount = invalidItems.filter((i) => i.reasonCode === 'refused-by-cart').length;
  const hasNonCartRejection = invalidItems.some((i) => i.reasonCode !== 'refused-by-cart');
  // EWM-949 — true when EVERY rejected row was rejected by the PARSER (nothing
  // was even looked up). "Please check SKUs and re-upload" is the wrong advice
  // then: the SKUs were never the problem, the file's shape was.
  // PR #1170 review (suggestion 4) — `refused-by-cart` is an add-time reason,
  // not a parse one, so it is excluded here too. (This predicate is only read
  // on the pre-add preview, which a refused row can never reach, but the name
  // must not assert something it does not check.)
  const allRejectedByParse =
    invalidItems.length > 0 &&
    warningItems.length === 0 &&
    invalidItems.every(
      (i) => i.reasonCode !== undefined && i.reasonCode !== 'sku-not-found' && i.reasonCode !== 'refused-by-cart',
    );
  // EWM-944 — accepted rows that will BACK-ORDER rather than ship from stock.
  // The green total counts them, which on its own reads as "12 items ready"
  // when 5 of them are not. AC 4 asks for accepted OOS lines to be identified
  // distinctly; the per-row badge did that but the summary did not.
  const backOrderCount = parsedItems.filter((i) => i.status === 'valid' && i.willBackOrder).length;
  // EWM-949 — accepted rows whose quantity the file never carried (a codes-only
  // upload). Informational, never a rejection: the whole point of this ticket
  // is that nothing is applied to an upload without being said out loud.
  const quantityDefaultedCount = parsedItems.filter((i) => i.status === 'valid' && i.quantityDefaulted).length;

  const processFile = useCallback(
    // EWM-942 — `mapping` is supplied only on the SECOND pass, after the
    // customer has chosen their columns in the preflight step (AC-8).
    async (uploaded: File, mapping?: UploadFileMapping) => {
      setUploadError(null);
      setPendingMapping(null);
      setIgnoredRowCount(0);
      // EWM-606 — reject unsupported formats up front (drag-drop bypasses the
      // file input's `accept`) with a clear inline message, before any read.
      // EWM-949 — the same gate the Quick Order panel now uses; the two had
      // drifted apart (see utils/file/accepted-upload-file).
      if (!isAcceptedUploadFile(uploaded)) {
        setUploadError(translate('quick-order.error-unsupported-format'));
        return;
      }
      // EWM-606 — reject >5MB before reading; otherwise readAsText + parse on a
      // huge file blocks the main thread and the page hangs/goes blank.
      if (uploaded.size > MAX_FILE_SIZE_BYTES) {
        setUploadError(translate('quick-order.error-file-too-large'));
        return;
      }
      // EWM-646 — an empty (0-byte) file has no rows regardless of type. The
      // CSV-only parse below can't catch a blank XLS/XLSX (those are parsed
      // server-side per EWM-247), so an empty spreadsheet previously slipped
      // through with no feedback. Guard on size up front for EVERY accepted
      // type so a blank file always surfaces the empty-file error.
      if (uploaded.size === 0) {
        setUploadError(translate('quick-order.error-empty-file'));
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }
      setFile(uploaded);
      setParsedItems([]);
      setAddedToCart(false);
      setParsing(true);
      try {
        // EWM-942 — TWO parsers, one result shape.
        //
        // `.csv` keeps its client-side parse, untouched (AC-2): EWM-949 has just
        // rewritten it with per-row reasons and 1-based line numbers, and routing
        // CSV through the server would put "behaves exactly as today" at risk for
        // no gain.
        //
        // `.xls` / `.xlsx` go to the `quickOrder/uploadFile` BFF action, because
        // a browser cannot read a BIFF or OOXML workbook without shipping a
        // spreadsheet library to every visitor. Both paths converge on
        // `ParsedUploadItem[]`, so the catalogue resolution, the availability
        // badging and EWM-944's back-order marking below are shared (AC-15).
        const isCsv = isCsvUploadFile(uploaded);
        let rawItems: ParsedUploadItem[];
        let delimiter = 'none';

        if (isCsv) {
          const parsed = await parseCsv(uploaded);
          rawItems = parsed.items;
          delimiter = parsed.delimiter;
        } else {
          // Fail closed. Without a parser wired in, a spreadsheet upload must
          // say so — resolving to an empty preview is the "reported success,
          // added nothing" defect EWM-949 exists to remove.
          if (!parseUploadFile) {
            setFile(null);
            setUploadError(translate('quick-order.error-upload-fail'));
            if (fileInputRef.current) fileInputRef.current.value = '';
            reportZeroAccepted([], 'spreadsheet', 'no-server-parser');
            return;
          }

          let response;
          try {
            const contentBase64 = await readAsBase64(uploaded);
            response = await parseUploadFile({ fileName: uploaded.name, contentBase64, mapping });
          } catch {
            // A read or transport failure is OURS, not the customer's file's.
            setFile(null);
            setUploadError(translate('quick-order.error-upload-fail'));
            if (fileInputRef.current) fileInputRef.current.value = '';
            reportZeroAccepted([], 'spreadsheet', 'upload-failed');
            return;
          }

          if (!response.success) {
            setFile(null);
            // AC-16 — a file we could not OPEN gets its own sentence. Telling
            // someone with a corrupt .xlsx to check their SKU column sends them
            // looking in the wrong place.
            // EWM-942 round 1 (R1-1) — and a file with more rows than the
            // parser will read gets a third sentence: it is not corrupt and it
            // is not 5MB, so both of the others would send the customer after
            // the wrong problem.
            // AC-14 — an oversized file gets the SAME message the CSV path
            // shows, because it is the same limit.
            setUploadError(
              response.errorName === 'upload_file_unreadable_error'
                ? translate('quick-order.error-unreadable-file')
                : response.errorName === 'upload_file_too_many_rows_error'
                  ? // PR #1176 review (S3) — the sentence no longer prints a
                    // specific row count. `MAX_SHEET_ROWS` is a 0-based INDEX, so
                    // "up to 5000" was off by one against the real boundary
                    // (5,001 rows are accepted). The customer only needs "too
                    // many, split it", which is exactly true and cannot drift.
                    translate('quick-order.error-too-many-rows')
                  : // PR #1176 review — the zip-bomb guard. It is neither corrupt nor
                    // over the 5MB upload cap (a zip bomb is small on the wire), so it
                    // needs its own sentence rather than borrowing one that points the
                    // customer at the wrong problem.
                    response.errorName === 'upload_file_expands_too_large_error'
                    ? translate('quick-order.error-file-expands-too-large')
                    : isPayloadTooLarge(response)
                      ? translate('quick-order.error-file-too-large')
                      : translate('quick-order.error-upload-fail'),
            );
            if (fileInputRef.current) fileInputRef.current.value = '';
            reportZeroAccepted([], 'spreadsheet', response.errorName ?? 'upload-rejected');
            return;
          }

          const parsed = response.result;
          if (parsed.needsMapping) {
            // AC-6 — a required column could not be identified. Hold the file
            // and show the preflight; NO line is processed until the customer
            // confirms, and cancelling drops the file entirely (AC-8).
            setPendingMapping({
              file: uploaded,
              columns: parsed.columns,
              headerRowIndex: parsed.headerRowIndex,
            });
            return;
          }

          setIgnoredRowCount(parsed.ignoredRows);
          rawItems = [
            ...parsed.valid.map((row) => ({
              code: row.code,
              quantity: row.quantity,
              status: 'valid' as const,
              line: row.line,
              ...(row.quantityDefaulted ? { quantityDefaulted: true } : {}),
            })),
            ...parsed.invalid.map((row) => ({
              code: row.code,
              quantity: row.quantity,
              status: 'invalid' as const,
              line: row.line,
              reasonCode: row.reasonCode,
            })),
          ].sort((a, b) => (a.line ?? 0) - (b.line ?? 0));
          delimiter = 'spreadsheet';
        }

        // EWM-949 — the parser stays i18n-free and reports a machine reason
        // code; the customer-facing sentence is attached here, at the one place
        // that has `translate`. EWM-942 — the spreadsheet path reports the SAME
        // reason codes, so it reuses this map unchanged.
        const items = rawItems.map((item) =>
          item.reasonCode && PARSE_REASON_KEYS[item.reasonCode]
            ? { ...item, error: translate(PARSE_REASON_KEYS[item.reasonCode] as string) }
            : item,
        );
        // EWM-646 — any accepted file we can't extract a product row from is
        // treated as empty: surface an inline error explaining the expected
        // format and pointing at the template (the template link stays in the
        // footer), clear the file, and keep the drop zone.
        //
        // This must fire for XLS/XLSX too, not just CSV. A blank spreadsheet
        // saved by Excel/Edge is a valid ZIP archive (~1.5KB) — NOT 0 bytes — so
        // the size===0 fast-path above misses it.
        //
        // EWM-942 — a spreadsheet now reaches here only when the server READ it
        // and found no order lines, which is the genuinely-empty case this guard
        // was always meant to cover. A file it could not open at all took the
        // AC-16 branch above, with its own message.
        if (items.length === 0) {
          setFile(null);
          setUploadError(translate('quick-order.error-empty-file'));
          if (fileInputRef.current) fileInputRef.current.value = '';
          // EWM-949 AC-9 — a file that yielded no rows at all is still an upload
          // that added nothing, and is the hardest case to diagnose from a
          // support call ("it just said the file was empty").
          reportZeroAccepted([], delimiter, 'no-rows');
          return;
        }
        // EWM-605 — resolve the parsed codes against the catalogue so rows that
        // don't exist are surfaced as failed (instead of optimistically "valid"
        // and silently dropped on add). Codes not returned by the resolver are
        // marked invalid with a reason; only valid rows get added to the cart.
        // EWM-645 — resolved rows also carry a stock status: out-of-stock /
        // due-soon rows become warnings (badged, kept on the page, excluded
        // from the cart add) rather than being added silently.
        // Resolver errors fall back to optimistic-valid so a transient lookup
        // failure doesn't block a legitimate upload.
        // EWM-949 — rows the PARSER already rejected are excluded from the
        // lookup and kept exactly as they are. Sending them would look up a
        // code that was never read (often an empty string) and come back
        // "SKU not found" — a reason that blames the catalogue for a fault in
        // the file, which was the one misleading message this drawer produced.
        let finalItems: ParsedUploadItem[] = items;
        const resolvable = items.filter((i) => i.status !== 'invalid');
        // Nothing survived the parse — there is no code to look up, so the
        // catalogue is not called at all.
        if (resolveSkus && resolvable.length > 0) {
          try {
            const resolved = await resolveSkus(resolvable.map((i) => i.code));
            const byCode = new Map(resolved.map((r) => [r.sku.toLowerCase(), r]));
            finalItems = items.map((item) => {
              if (item.status === 'invalid') return item;
              const match = byCode.get(item.code.toLowerCase());
              if (!match) {
                return {
                  ...item,
                  status: 'invalid' as const,
                  reasonCode: 'sku-not-found' as const,
                  error: translate('quick-order.sku-not-found'),
                };
              }
              // EWM-944 — apply the EWM-360 back-order rules per row. A
              // back-order-allowed customer's out-of-stock row is ACCEPTED at
              // the full requested quantity and flagged as back-ordering; a
              // GO (removed-from-sale) SKU stays rejected for everyone, with a
              // reason that distinguishes it from a plain out-of-stock line.
              const availability = resolveUploadLineAvailability({
                inStock: match.inStock ?? match.stockStatus === 'in-stock',
                availableQuantity: match.availableQuantity,
                requestedQuantity: item.quantity,
                removedFromSale: match.removedFromSale,
                backOrdersAllowed,
                paymentTerms,
              });

              if (availability.accepted) {
                return {
                  ...item,
                  status: 'valid' as const,
                  stockStatus: match.stockStatus,
                  willBackOrder: availability.backOrder,
                };
              }

              return {
                ...item,
                status: 'warning' as const,
                stockStatus: match.stockStatus,
                reasonCode:
                  availability.reason === 'excluded-from-back-order' || availability.reason === 'removed-from-sale'
                    ? availability.reason
                    : undefined,
                // An in-stock GO row gets its own message: "cannot be
                // back-ordered" explains nothing about an item that is on the
                // shelf and simply no longer sold.
                error:
                  availability.reason === 'excluded-from-back-order'
                    ? translate('quick-order.excluded-from-back-order')
                    : availability.reason === 'removed-from-sale'
                      ? translate('quick-order.removed-from-sale')
                      : undefined,
              };
            });
          } catch {
            // A transient lookup failure leaves the parsed rows as they are —
            // including the parse rejections, which are facts about the file
            // and have nothing to do with the catalogue being reachable.
            finalItems = items;
          }
        }
        setParsedItems(finalItems);
        // AC-3 / AC-9 — an upload that accepted nothing is the ticket's
        // headline failure. Report it so a recurring format problem surfaces
        // without a customer having to ring support.
        if (!finalItems.some((i) => i.status === 'valid')) {
          reportZeroAccepted(finalItems, delimiter);
        }
      } finally {
        setParsing(false);
      }
    },
    [resolveSkus, translate, backOrdersAllowed, paymentTerms, parseUploadFile],
  );

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === 'dragenter' || e.type === 'dragover');
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      const dropped = e.dataTransfer.files[0];
      if (dropped) processFile(dropped);
    },
    [processFile],
  );

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selected = e.target.files?.[0];
      if (selected) processFile(selected);
    },
    [processFile],
  );

  const handleReset = useCallback(() => {
    setFile(null);
    // EWM-942 (AC-8) — cancelling the mapping step abandons the upload, so the
    // held file goes with it and no line is ever processed.
    setPendingMapping(null);
    setIgnoredRowCount(0);
    setParsedItems([]);
    setAddedToCart(false);
    setUploadError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleAddToCart = useCallback(async () => {
    if (validItems.length === 0) return;
    setAddingToCart(true);
    // EWM-605 — honor the add result. The add can fail (e.g. a price/stock
    // rejection); previously success was shown unconditionally even when the
    // cart wasn't updated. On failure surface a toast and stay on the preview
    // so the user can retry rather than seeing a false "Added to cart". The
    // try/finally guarantees the button never gets stuck loading if the add
    // rejects unexpectedly.
    try {
      const res = (await addItem?.(validItems.map((i) => ({ sku: i.code, count: i.quantity })))) as
        { success?: boolean; message?: string; failed?: Array<{ sku?: string; reason?: string }> } | undefined;
      if (res && res.success === false) {
        toast.error(res.message || translate('common.something-went-wrong'));
        return;
      }
      // EWM-949 (PR #1170 review) — `success: true` is NOT "every row went in".
      // `quickOrder/addItems` adds what it can and returns the rest in
      // `failed[]`, and its own docstring says the drawer is meant to surface
      // them. Reading only `success` is the ticket's own defect class one screen
      // later: a row refused at add time was still counted in "N products added
      // successfully".
      //
      // It is genuinely reachable, not theoretical. `resolveSkus` is a CATALOGUE
      // check (existence + stock); `failed[]`'s `unavailable` means no
      // Standalone Price for THIS dealer's channel. Different checks — so a row
      // can show green in the preview and still be refused here.
      //
      // Refused rows are reclassified as invalid rather than toasted, so they
      // flow into the SAME partial-success screen the unmatched rows already
      // use: the counts stay truthful and each refused row gets an UnmatchedRow
      // with a search box to fix it. `UnmatchedRow.handleAdd` does this for the
      // single-row path; this is the bulk path matching it.
      const failed = res?.failed ?? [];
      if (failed.length > 0) {
        const refused = new Set(failed.map((f) => f?.sku?.toLowerCase()).filter((sku): sku is string => !!sku));
        // PR #1170 review (suggestion 1) — measure success by how many rows we
        // actually RECLASSIFY, not by the size of a Set of SKUs. A Set collapses
        // a SKU that appears on two lines (both genuinely refused) into one, so
        // `refused.size < failed.length` fired a spurious toast on a duplicate,
        // and let an entry whose SKU matched no row we sent slip through
        // silently. Counting reclassified rows closes both.
        const rowsToReclassify = validItems.filter((item) => refused.has(item.code.toLowerCase()));
        const reclassifiedCount = rowsToReclassify.length;
        // A `failed[]` entry we cannot line up with a row we sent (absent, or
        // normalised differently upstream) must NOT fall through to a clean
        // success — we know something was refused, we just can't say which row.
        if (reclassifiedCount < failed.length) {
          toast.error(
            translate('quick-order.some-refusals-unattributed', { count: failed.length - reclassifiedCount }),
          );
        }
        if (reclassifiedCount > 0) {
          setParsedItems((prev) =>
            prev.map((item) =>
              item.status === 'valid' && refused.has(item.code.toLowerCase())
                ? {
                    ...item,
                    status: 'invalid' as const,
                    reasonCode: 'refused-by-cart' as const,
                    error: translate('quick-order.error-row-refused-by-cart'),
                  }
                : item,
            ),
          );
        }
        // PR #1170 review (suggestion 3) — an upload the cart refuses in full
        // ends at the same customer outcome as a zero-accepted parse (nothing in
        // the cart) and is logged nowhere: `reportZeroAccepted` fires only on the
        // parse path, and the BFF returns `success: true` with no log line. Push
        // the same AC-9 signal, keyed on `refused-by-cart`, when every valid row
        // was refused.
        if (reclassifiedCount === validItems.length) {
          reportZeroAccepted(
            rowsToReclassify.map((item) => ({ ...item, status: 'invalid' as const, reasonCode: 'refused-by-cart' })),
            'cart-add',
          );
        }
      }
      setAddedToCart(true);
    } catch {
      toast.error(translate('common.something-went-wrong'));
    } finally {
      setAddingToCart(false);
    }
  }, [addItem, validItems, translate]);

  return (
    <Drawer
      id={drawerId}
      direction="right"
      isOpen={isOpen}
      headline={translate('quick-order.upload-drawer-title')}
      onClose={onClose}
      // EWM-931 — guard the close paths (close control, scrim, Escape) while the
      // customer has upload data that is NOT yet in the cart, so an accidental
      // close can't silently discard a parsed file. Rows already added to the
      // cart are safe, so the success view is not guarded. `onDiscard` clears
      // the parsed state so the next open starts clean.
      confirmOnDirty
      confirmRequiresChoice
      // EWM-942 — a file held at the preflight step is unsaved work too: closing
      // the drawer over it discards an upload the customer has not finished.
      isDirty={(parsedItems.length > 0 || !!file || !!pendingMapping) && !addedToCart && !addingToCart}
      onDiscard={() => {
        handleReset();
        onClose?.();
      }}
      // EWM-560 — match the Quick Order tray width (max-w-lg) for a consistent
      // experience when switching between the two trays.
      className="!w-screen !max-w-lg"
    >
      {pendingMapping ? (
        /* EWM-942 (AC-6..AC-8) — a required column could not be identified, so
           the customer maps their own. Confirming re-parses the SAME file with
           their choice; cancelling abandons the upload without processing any
           lines. This branch comes FIRST so no preview, summary or add button
           can render while a file is still waiting to be mapped. */
        <ColumnMapping
          columns={pendingMapping.columns}
          headerRowIndex={pendingMapping.headerRowIndex}
          fileName={pendingMapping.file.name}
          onConfirm={(mapping) => {
            void processFile(pendingMapping.file, mapping);
          }}
          onCancel={handleReset}
        />
      ) : addedToCart && invalidItems.length === 0 && warningItems.length === 0 ? (
        <div className="flex flex-col items-center gap-6 px-6 py-20 text-center">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-green-100">
            <CheckCircleIcon className="h-12 w-12 text-green-600" aria-hidden="true" />
          </div>
          <div>
            <h3 className="text-20 text-primary font-bold">{translate('quick-order.added-to-cart')}</h3>
            <p className="text-14 mt-2 text-gray-600">
              {translate('quick-order.added-to-cart-desc', { count: validItems.length })}
            </p>
            {backOrderCount > 0 && (
              <p className="text-13 mt-1 text-amber-700">
                {translate('quick-order.of-which-back-order', { count: backOrderCount })}
              </p>
            )}
          </div>
          <div className="flex w-full max-w-xs flex-col gap-3">
            <button
              type="button"
              onClick={handleReset}
              className="text-14 text-primary block w-full rounded-lg border border-gray-200 px-5 py-3 font-semibold transition-colors hover:bg-gray-50"
            >
              {translate('quick-order.upload-another-file')}
            </button>
            <Link
              href="/cart"
              onClick={onClose}
              className="bg-primary hover:bg-primary/90 text-14 block w-full rounded-lg px-5 py-3 text-center font-semibold text-white transition-colors"
            >
              {translate('quick-order.view-cart')}
            </Link>
          </div>
        </div>
      ) : addedToCart ? (
        /* EWM-605 — partial success: the valid rows were added, but unmatched
           rows remain. Surface them (instead of replacing the whole drawer with
           a success screen) so the user can find the correct product via the
           autocomplete and add it manually.
           EWM-645 — out-of-stock / due-soon rows were also held back; list them
           (with a stock badge) so it's clear they weren't added and why. */
        <div className="flex min-h-full flex-col">
          <div className="space-y-4 p-5">
            {/* EWM-949 (PR #1170 review) — the add can refuse every valid row
                (see `handleAddToCart`), which lands here with nothing added.
                A green tick over "0 products added successfully" would be the
                same unearned success this ticket exists to remove, so the
                zero-added case gets its own banner and icon. */}
            {validItems.length === 0 ? (
              <div className="flex items-start gap-3 rounded-lg bg-red-50 p-4" role="alert">
                <ExclamationTriangleIcon className="h-6 w-6 shrink-0 text-red-600" aria-hidden="true" />
                <p className="text-13 text-gray-700">
                  {/* PR #1170 review (blocker) — attribute the refusal correctly.
                      When every rejected row was refused by the cart, name the
                      count and the account-availability remedy. When the causes
                      are mixed (some rows never reached the cart — a malformed
                      quantity, an unknown SKU), a neutral sentence that sends the
                      customer to the per-row reasons, not all to their account. */}
                  {hasNonCartRejection
                    ? translate('quick-order.nothing-added-mixed')
                    : translate('quick-order.nothing-added-refused', { count: refusedByCartCount })}
                </p>
              </div>
            ) : (
              <div className="flex items-start gap-3 rounded-lg bg-green-50 p-4">
                <CheckCircleIcon className="h-6 w-6 shrink-0 text-green-600" aria-hidden="true" />
                <p className="text-13 text-gray-700">
                  {invalidItems.length > 0
                    ? translate('quick-order.partial-success-message', {
                        added: validItems.length,
                        unmatched: invalidItems.length,
                      })
                    : translate('quick-order.added-to-cart-desc', { count: validItems.length })}
                </p>
              </div>
            )}
            {/* EWM-645 — unavailable (out-of-stock / due-soon) rows: badged,
                not added, kept visible so the user knows what was skipped. */}
            {warningItems.length > 0 && (
              <div className="space-y-3">
                <p className="text-13 font-medium text-amber-700">
                  {translate('quick-order.some-unavailable', { count: warningItems.length })}
                </p>
                {warningItems.map((item, index) => (
                  <div
                    key={`${item.code}-${index}`}
                    className="flex items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50/60 p-3"
                  >
                    <div className="min-w-0">
                      <p className="text-13 font-semibold text-gray-800">{item.code}</p>
                      <p className="text-12 text-gray-500">
                        {translate('quick-order.qty-column')} {item.quantity}
                      </p>
                      {/* AC 6 — the distinguishing reason must survive the add.
                          This row rendered only the StockBadge, so a GO row lost
                          the one line explaining why it was held back, at exactly
                          the moment the customer goes looking for it. */}
                      {item.error && <p className="text-12 mt-0.5 text-amber-700">{item.error}</p>}
                    </div>
                    {item.stockStatus && <StockBadge status={item.stockStatus} />}
                  </div>
                ))}
              </div>
            )}
            {invalidItems.length > 0 && (
              <div className="space-y-3">
                {invalidItems.map((item, index) => (
                  <UnmatchedRow
                    key={`${item.code}-${index}`}
                    code={item.code}
                    quantity={item.quantity}
                    // EWM-949 — a row the PARSER rejected is not a missing SKU;
                    // it carries its own reason and its line number so the
                    // customer can find it in the file.
                    line={item.line}
                    reason={item.error}
                    addItemDisabled={addItemDisabled}
                    searchProducts={searchProducts}
                    addItem={addItem}
                  />
                ))}
              </div>
            )}
          </div>
          <div className="sticky bottom-0 mt-auto border-t border-gray-200 bg-white p-5">
            <div className="flex w-full flex-col gap-3">
              <button
                type="button"
                onClick={handleReset}
                className="text-14 text-primary block w-full rounded-lg border border-gray-200 px-5 py-3 font-semibold transition-colors hover:bg-gray-50"
              >
                {translate('quick-order.upload-another-file')}
              </button>
              <Link
                href="/cart"
                onClick={onClose}
                className="bg-primary hover:bg-primary/90 text-14 block w-full rounded-lg px-5 py-3 text-center font-semibold text-white transition-colors"
              >
                {translate('quick-order.view-cart')}
              </Link>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex min-h-full flex-col">
          <div className="p-5">
            {/* EWM-532 — the code-type selector (Electus SKU vs Your Product
                Code) was removed; uploads are always interpreted as Electus
                SKUs. Keep a short hint so the expected format stays clear. */}
            <p className="text-12 mb-6 text-gray-500">{translate('quick-order.electus-sku-desc')}</p>

            {/* Drop zone */}
            {!file && (
              <div
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                className={`rounded-lg border-2 border-dashed p-8 text-center transition-colors ${
                  dragActive ? 'border-primary bg-primary/5' : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <ArrowUpTrayIcon className="mx-auto mb-4 h-12 w-12 text-gray-400" aria-hidden="true" />
                <p className="text-14 mb-2 font-semibold text-gray-700">{translate('quick-order.drag-drop-file')}</p>
                <p className="text-13 mb-4 text-gray-500">{translate('quick-order.or')}</p>
                <label>
                  <span className="bg-primary hover:bg-primary/90 text-14 cursor-pointer rounded-lg px-5 py-2.5 font-semibold text-white transition-colors">
                    {translate('quick-order.browse-files')}
                  </span>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleFileInput}
                    className="sr-only"
                  />
                </label>
                <p className="text-12 mt-4 text-gray-400">{translate('quick-order.supported-formats')}</p>
              </div>
            )}

            {/* EWM-606 — inline rejection message (unsupported format / >5MB). */}
            {!file && uploadError && (
              <p role="alert" className="text-13 mt-3 font-medium text-red-600">
                {uploadError}
              </p>
            )}

            {/* File uploaded — preview */}
            {file && (
              <div className="space-y-4">
                {/* File info row */}
                <div className="flex items-center justify-between rounded-lg bg-gray-50 p-4">
                  <div className="flex items-center gap-3">
                    <DocumentArrowUpIcon className="text-primary h-8 w-8 shrink-0" aria-hidden="true" />
                    <div>
                      <p className="text-14 font-semibold text-gray-800">{file.name}</p>
                      <p className="text-12 text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleReset}
                    aria-label={translate('common.close')}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                {parsing && (
                  <div className="py-6 text-center">
                    <div
                      className="border-primary mx-auto h-8 w-8 animate-spin rounded-full border-2 border-t-transparent"
                      aria-hidden="true"
                    />
                  </div>
                )}

                {/* Parsed preview table */}
                {!parsing && parsedItems.length > 0 && (
                  <>
                    <div className="overflow-hidden rounded-lg border border-gray-200">
                      <div className="border-b border-gray-200 bg-gray-50 px-4 py-3">
                        <p className="text-14 font-semibold text-gray-800">
                          {translate('quick-order.preview-title', { count: parsedItems.length })}
                        </p>
                      </div>
                      <div className="max-h-64 overflow-y-auto">
                        <table className="w-full">
                          <thead className="sticky top-0 bg-gray-50">
                            <tr>
                              <th className="text-12 px-4 py-2 text-left font-semibold text-gray-600">
                                {translate('quick-order.code-column')}
                              </th>
                              <th className="text-12 px-4 py-2 text-center font-semibold text-gray-600">
                                {translate('quick-order.qty-column')}
                              </th>
                              <th className="text-12 px-4 py-2 text-right font-semibold text-gray-600">
                                {translate('quick-order.status-column')}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {parsedItems.map((item, index) => (
                              <tr key={index} className="border-t border-gray-100">
                                <td className="px-4 py-3">
                                  <div className="text-14 font-medium text-gray-800">{item.code}</div>
                                  {/* EWM-949 — AC-5: "enough detail to locate it
                                      in their file". The line number counts the
                                      header and any blank lines, so it matches
                                      the row number in the customer's
                                      spreadsheet. It is also the ONLY handle on
                                      a row whose code cell was empty. */}
                                  {item.status === 'invalid' && item.line !== undefined && (
                                    <div className="text-12 text-gray-500">
                                      {translate('quick-order.row-line', { line: item.line })}
                                    </div>
                                  )}
                                  {item.productName && <div className="text-12 text-gray-500">{item.productName}</div>}
                                  {item.error && <div className="text-12 text-red-500">{item.error}</div>}
                                </td>
                                {/* A row we could not read a quantity from has
                                    none — showing the old invented "1" is what
                                    made a malformed file look orderable. */}
                                <td className="px-4 py-3 text-center text-gray-700">
                                  {item.quantity > 0 ? item.quantity : '—'}
                                </td>
                                <td className="px-4 py-3 text-right">
                                  {/* EWM-645 — resolved rows show a traffic-light stock badge;
                                      unmatched (not-found) rows keep the warning triangle;
                                      the optimistic no-resolver path keeps the plain check. */}
                                  {item.status === 'invalid' ? (
                                    <ExclamationTriangleIcon
                                      className="inline h-5 w-5 text-amber-500"
                                      aria-hidden="true"
                                    />
                                  ) : item.willBackOrder ? (
                                    /* EWM-944 — an ACCEPTED out-of-stock row. Badged
                                       distinctly from an in-stock row so the customer
                                       knows it will back-order before proceeding to
                                       cart (AC 4). The whole requested quantity is
                                       accepted — never split into available +
                                       back-ordered parts. */
                                    <span className="text-11 inline-block rounded-full bg-amber-50 px-3 py-1 font-semibold text-amber-700">
                                      {translate('quick-order.will-back-order')}
                                    </span>
                                  ) : item.stockStatus ? (
                                    <StockBadge status={item.stockStatus} />
                                  ) : (
                                    <CheckCircleIcon className="inline h-5 w-5 text-green-500" aria-hidden="true" />
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* EWM-949 — AC-4: EVERY result states both numbers, in
                        words, not just as two coloured tiles (the amber tile
                        renders only when something was skipped, so "0 rejected"
                        was never stated). Red when nothing was accepted, so the
                        summary itself never reads as a success. */}
                    <p
                      className={`text-13 font-semibold ${validItems.length === 0 ? 'text-red-600' : 'text-gray-700'}`}
                    >
                      {translate('quick-order.upload-summary', {
                        accepted: validItems.length,
                        rejected: skippedCount,
                      })}
                    </p>

                    {/* EWM-949 — a codes-only file is ACCEPTED, and the
                        defaulted quantity is stated plainly. Neutral styling,
                        below the summary: this is a note on a successful
                        upload, not a rejection, and must not read as one. */}
                    {quantityDefaultedCount > 0 && (
                      <p className="text-13 text-gray-600">
                        {translate('quick-order.quantity-defaulted', { count: quantityDefaultedCount })}
                      </p>
                    )}

                    {/* EWM-942 (AC-11) — blank, note and totals rows are IGNORED
                        rather than rejected, because the customer wrote them on
                        purpose. Stating the count keeps "ignored" from meaning
                        "silently dropped": accepted + rejected + skipped accounts
                        for every row in the file. */}
                    {ignoredRowCount > 0 && (
                      <p className="text-13 text-gray-600">
                        {translate('quick-order.rows-ignored', { count: ignoredRowCount })}
                      </p>
                    )}

                    {/* Valid / not-added summary. EWM-645 — the amber box now
                        counts everything held back from the cart: unmatched
                        rows AND out-of-stock / due-soon rows. */}
                    <div className="flex gap-4">
                      <div className="flex-1 rounded-lg bg-green-50 p-3">
                        <p className="text-12 font-semibold text-green-600">
                          {translate('quick-order.valid-items-label')}
                        </p>
                        <p className="text-20 font-bold text-green-700">{validItems.length}</p>
                        {backOrderCount > 0 && (
                          <p className="text-12 mt-0.5 text-amber-700">
                            {translate('quick-order.of-which-back-order', { count: backOrderCount })}
                          </p>
                        )}
                      </div>
                      {skippedCount > 0 && (
                        <div className="flex-1 rounded-lg bg-amber-50 p-3">
                          <p className="text-12 font-semibold text-amber-600">
                            {translate('quick-order.not-added-label')}
                          </p>
                          <p className="text-20 font-bold text-amber-700">{skippedCount}</p>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Footer block — pinned to drawer bottom via mt-auto, stays visible while scrolling via sticky */}
          <div className="sticky bottom-0 mt-auto bg-white">
            {file && !parsing && parsedItems.length > 0 && validItems.length > 0 && (
              <div className="border-t border-gray-200 p-5">
                <Button
                  loading={addingToCart}
                  onClick={handleAddToCart}
                  disabled={addItemDisabled}
                  size="m"
                  variant="primary"
                  className="w-full !rounded-lg !px-5 !font-semibold"
                >
                  {translate('quick-order.add-n-items-to-cart', { count: validItems.length })}
                </Button>
                {skippedCount > 0 && (
                  <p className="text-12 mt-2 text-center text-gray-500">
                    {translate('quick-order.n-items-skipped', { count: skippedCount })}
                  </p>
                )}
              </div>
            )}
            {/* EWM-605 — "All SKUs invalid" scenario: nothing to add, so prompt
                the user to check the SKUs and re-upload (per the Quick Order
                error-handling spec) instead of showing a disabled "Add 0" button.
                EWM-645 — when nothing is addable because every resolved row is
                out of stock / due soon, say so specifically rather than implying
                the SKUs are wrong. The rows stay visible (badged) in the preview. */}
            {file && !parsing && parsedItems.length > 0 && validItems.length === 0 && (
              <div className="border-t border-gray-200 p-5">
                {/* EWM-949 — AC-3. A zero-accepted upload is a FAILURE, and the
                    message has to name the right fault: a file we could not
                    read is not a list of bad SKUs. */}
                <p role="alert" className="text-13 text-center font-medium text-red-600">
                  {invalidItems.length === 0 && warningItems.length > 0
                    ? translate('quick-order.all-unavailable')
                    : allRejectedByParse
                      ? translate('quick-order.all-lines-rejected', { count: invalidItems.length })
                      : translate('quick-order.no-valid-products')}
                </p>
                {/* EWM-949 — AC-8. Correct-and-re-upload without restarting the
                    flow. `handleReset` already clears the file, the parsed rows
                    and the input's value; it was only reachable from the small
                    X on the file row, which reads as "close", not "try again". */}
                <button
                  type="button"
                  onClick={handleReset}
                  className="text-14 text-primary mt-4 block w-full rounded-lg border border-gray-200 px-5 py-3 font-semibold transition-colors hover:bg-gray-50"
                >
                  {translate('quick-order.upload-another-file')}
                </button>
              </div>
            )}
            <div className="border-t border-gray-200 p-5">
              <p className="text-12 mb-2 text-gray-500">{translate('quick-order.need-template')}</p>
              <a href={downloadLink} download className="text-primary text-13 font-semibold hover:underline">
                {translate('quick-order.download-csv-template')}
              </a>
            </div>
          </div>
        </div>
      )}
    </Drawer>
  );
};

export default QuickOrderUpload;
